import { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  UserIcon,
  BriefcaseIcon,
} from "@heroicons/react/24/outline";

// components
import { UserAvatar } from "../../components/commons/user.avatar";
import { StarRating } from "../../components/commons/star.rating";
import { SectionSubHeader } from "../../components/layouts/section.subheader";

// reducers
import { fetchJobServiceReports } from "../../stores/jobs";

// utilities
import { classNames, openAssetKeyNewTab } from "../../utilities";

export const ScreenJobDetailsReports = () => {

  // hooks
  const dispatch = useDispatch();
  const { details, reports } = useSelector(context => context.jobs);
  const { user_id, job_id, service_type } = details;

  // state
  const [mounted, setMounted] = useState(false);
  const [loading, setLoading] = useState(false);

  const _reports = useMemo(() => {
    // sanity check
    if (reports.length === 0) return [];

    // breakdown
    if (service_type === 'faulty') {
      const before = reports.find(item => item.report_type === 'breakdown:before_checklist');
      const after = reports.find(item => item.report_type === 'breakdown:after_checklist');
      return [
        {
          ...before, 
          name: 'Checklist: Before',
          report_items: [
            {...before.report['before_general_1']},
            {...before.report['before_general_2']},
            {...before.report['before_short_3']},
            {...before.report['before_short_4']},
            {...before.report['before_leakage_5']},
            {...before.report['before_leakage_6']},
            {...before.report['before_open_7']},
            {...before.report['before_faulty_types']},
            {...before.report['before_others_anomalies']}
          ]
        },
        {
          ...after, 
          name: 'Checklist: After',
          report_items: [
            {...after.report['after_fix_1']},
            {...after.report['after_fix_2']},
            {...after.report['after_fix_3']}
          ]
        }
      ]
    }

    // breakdown
    if (service_type === 'inspection') {
      const safety = reports.find(item => item.report_type === 'inspection:safety_checklist');
      const testing = reports.find(item => item.report_type === 'inspection:testing_checklist');
      const conclusion = reports.find(item => item.report_type === 'inspection:conclusion_remarks');
      return [
        {
          ...safety, 
          name: 'Checklist: Safety',
          report_items: [
            {...safety.report['safety_1']},
            {...safety.report['safety_2']},
            {...safety.report['safety_3']},
            {...safety.report['safety_4']},
          ]
        },
        {
          ...testing, 
          name: 'Checklist: Testing',
          report_items: [
            {...testing.report['testing_1']},
            {...testing.report['testing_2']},
            {...testing.report['testing_3']},
            {...testing.report['testing_4']},
            {...testing.report['testing_5']},
            {...testing.report['testing_6']},
            {...testing.report['testing_7']},
            {...testing.report['testing_8']},
          ]
        },
        {
          ...conclusion, 
          name: 'Conclusion',
          report_items: [
            {...conclusion.report['proposal']},
          ]
        }
      ]
    }

    const conclusion = reports.find(item => item.report_type === 'common:conclusion_remarks');
    return [
      {
        ...conclusion, 
        name: 'Conclusion',
        report_items: [
          {...conclusion.report['proposal']},
        ]
      }
    ]

  }, [reports, service_type]);
  console.log('_reports', _reports, reports)

  const loaddata = async ({user_id, job_id}) => {
    try {
      setLoading(true);

      const res = await dispatch(fetchJobServiceReports({
        user_id,
        job_id
      })).unwrap();
      console.log('screens.jobs.details.reports@loaddata', 'res', res);

    } catch (err) {
      console.error('screens.jobs.details.reports@loaddata', err)
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    // sanity check
    if (!mounted) return;
    if (!user_id) return;
    if (!job_id) return;

    loaddata({user_id, job_id});
  }, [mounted, user_id, job_id]);

  useEffect(() => {
    setMounted(true)
  }, [])

  if (!loading && reports.length === 0) {
    return (
      <div className="h-[80vh] flex flex-col justify-center items-center bg-white rounded-2xl shadow-md">
        <p className="text-4xl text-slate-400 text-bold">No report was found</p>
      </div>
    )
  }

  if (loading && reports.length === 0) {
    return (
      <div className="h-[80vh] flex flex-col justify-center items-center bg-white rounded-2xl shadow-md">
        <p className="text-4xl text-slate-400 text-bold">Fetching reports...</p>
      </div>
    )
  }

  return (
    <div className="pb-[10vh] bg-white rounded-2xl shadow-md">
      {_reports.map(item => (
        <div key={`${item.resource_name}:${item.report_type}`} className="py-8 px-4 border-b border-slate-200">
          <SectionSubHeader title={item.name} Icon={UserIcon} />
          <div className="mt-8 flex flex-col">
            {item.report_items.map((report, index) => (
              <div className="py-4  border-b border-slate-100">
                <p className="text-base text-slate-600 font-medium">{index + 1}. {report.title.en}</p>
                <p className="text-base text-slate-400 font-light">{report.title.ms}</p>

                {report.type === 'checklist' &&
                  <div className="py-2">
                    <BadgeChecklistResult result={report.result} />
                  </div>
                }
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const HeaderKeyValue = ({label, value}) => {
  return (
    <div className="pr-8">
      <h4 className="text-sm text-slate-500 font-normal">{label}</h4>
      <p className="text-base text-slate-800 font-semibold">{value}</p>
    </div>
  )
}


const SideKeyValue = ({label, value}) => {
  return (
    <div className="pt-4 grid grid-cols-8">
      <h4 className="col-span-4 text-sm text-slate-500 font-normal">{label}</h4>
      <span className="col-span-4 text-sm text-slate-900 font-medium text-left">{value}</span>
    </div>
  )
}

const FilterSection = ({title, selected, onSelect = () => {}}) => {
  return (
    <div className={classNames(
      "px-8 py-1 mr-4 border border-slate-200 rounded-xl shadow-sm hover:cursor-pointer",
      selected ? 'bg-primary-500' : 'bg-slate-200',
    )}>
      <p className={classNames(
        "col-span-4 text-sm font-normal",
        selected ? 'text-white': 'text-slate-500'
      )}
      >
        {title}
      </p>
    </div>
  )
}

const BadgeChecklistResult = (props) => {
  // input
  const { result = null} = props;

  switch(result) {
    // residential
    case 'OK': return (
      <span className="inline-flex items-center rounded-md bg-green-500 px-10 py-1 text-xs font-medium text-white shadow-sm ring-1 ring-inset ring-green-500/10">
        OK
      </span>
    )
    default: return (
      <span className="inline-flex items-center rounded-md bg-red-500 px-10 py-1 text-xs font-medium text-white shadow-sm ring-1 ring-inset ring-red-500/10">
        NOT OK
      </span>
    )
  }
}